import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit, FiTrash, FiToggleLeft, FiToggleRight } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const CouponManagement = () => {
  const [coupons, setCoupons] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);

  // Helper function to get coupon status
  const getCouponStatus = (coupon) => {
    const today = new Date();
    const validUntil = new Date(coupon.validUntil);

    // ✅ Remove time part (set to 00:00:00)
    today.setHours(0, 0, 0, 0);
    validUntil.setHours(0, 0, 0, 0);

    if (!coupon.isActive) {
      return { status: 'Inactive', color: 'red' };
    }

    if (validUntil < today) {
      return { status: 'Expired', color: 'orange' };
    }

    return { status: 'Active', color: 'green' };
  };
  const [couponForm, setCouponForm] = useState({
    code: '',
    description: '',
    couponType: 'discount',
    discountType: 'percentage',
    discountValue: 0,
    minOrderValue: 0,
    maxDiscount: 0,
    bogo: {
      buyQuantity: 1,
      getQuantity: 1,
      itemId: ''
    },
    validFrom: '',
    validUntil: '',
    // usageLimit: 0,
    isActive: true
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    fetchCoupons();
    fetchMenuItems();
  }, []);

  const fetchCoupons = async () => {
    try {
      const response = await api.get('/vendor/coupons');
      setCoupons(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch coupons');
    } finally {
      setLoading(false);
    }
  };

  const fetchMenuItems = async () => {
    try {
      const response = await api.get('/vendor/menu-items');
      setMenuItems(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch menu items');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedCoupon) {
        await api.put(`/vendor/coupons/${selectedCoupon._id}`, couponForm);
        toast.success('Coupon updated successfully');
      } else {
        await api.post('/vendor/coupons', couponForm);
        toast.success('Coupon created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchCoupons();
    } catch (error) {
      toast.error('Failed to save coupon');
    }
  };

  const handleDelete = async (couponId) => {
    if (!window.confirm('Are you sure you want to delete this coupon?')) return;
    try {
      await api.delete(`/vendor/coupons/${couponId}`);
      toast.success('Coupon deleted successfully');
      fetchCoupons();
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  const toggleCouponStatus = async (couponId) => {
    try {
      await api.patch(`/vendor/coupons/${couponId}/toggle`);
      toast.success('Coupon status updated');
      fetchCoupons();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openModal = (coupon = null) => {
    if (coupon) {
      setSelectedCoupon(coupon);
      setCouponForm({
        code: coupon.code,
        description: coupon.description,
        couponType: coupon.couponType || 'discount',
        discountType: coupon.discountType,
        discountValue: coupon.discountValue,
        minOrderValue: coupon.minOrderValue,
        maxDiscount: coupon.maxDiscount,
        bogo: {
          buyQuantity: coupon.bogo?.buyQuantity || 1,
          getQuantity: coupon.bogo?.getQuantity || 1,
          itemId: coupon.bogo?.itemId?._id || ''
        },
        validFrom: coupon.validFrom?.split('T')[0] || '',
        validUntil: coupon.validUntil?.split('T')[0] || '',
        // usageLimit: coupon.usageLimit,
        isActive: coupon.isActive
      });
    }
    setShowModal(true);
  };

  const resetForm = () => {
    setCouponForm({
      code: '',
      description: '',
      couponType: 'discount',
      discountType: 'percentage',
      discountValue: 0,
      minOrderValue: 0,
      maxDiscount: 0,
      bogo: {
        buyQuantity: 1,
        getQuantity: 1,
        itemId: ''
      },
      validFrom: '',
      validUntil: '',
      // usageLimit: 0,
      isActive: true
    });
    setSelectedCoupon(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 p-4 md:p-6">
      {/* Page Header */}
      <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Coupon Management</h1>
            <p className="text-gray-600">Create and manage discount coupons</p>
          </div>
          <button
            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg transform transition-all duration-200 hover:scale-105 flex items-center gap-2"
            onClick={() => {
              setSelectedCoupon(null);
              setCouponForm({
                code: '',
                description: '',
                couponType: 'discount',
                discountType: 'percentage',
                discountValue: 0,
                minOrderValue: 0,
                maxDiscount: 0,
                bogo: {
                  buyQuantity: 1,
                  getQuantity: 1,
                  itemId: ''
                },
                validFrom: '',
                validUntil: '',
                isActive: true
              });
              setShowModal(true);
            }}
          >
            <span className="hidden sm:inline">+</span>
            <span className="sm:hidden">Add</span>
            <span className="hidden sm:inline">Add Coupon</span>
          </button>
        </div>
      </div>

      {/* Coupons Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {coupons.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((coupon) => (
          <div key={coupon._id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow duration-300">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🎟️</span>
                  <span className="font-bold text-lg">{coupon.code}</span>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCouponStatus(coupon).color === 'green'
                    ? 'bg-green-100 text-green-800'
                    : getCouponStatus(coupon).color === 'red'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-orange-100 text-orange-800'
                  }`}>
                  {getCouponStatus(coupon).status}
                </span>
              </div>
            </div>

            <div className="p-4">
              <p className="text-gray-600 text-sm mb-4 line-clamp-2">{coupon.description}</p>

              <div className="space-y-2">
                {coupon.couponType === 'discount' ? (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-gray-500">Discount</span>
                      <span className="text-sm font-bold text-blue-600">
                        {coupon.discountValue}{coupon.discountType === 'percentage' ? '%' : '₹'} off
                      </span>
                    </div>
                    {coupon.minOrderValue > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-medium text-gray-500">Min Order</span>
                        <span className="text-sm font-medium text-gray-700">₹{coupon.minOrderValue}</span>
                      </div>
                    )}
                    {coupon.maxDiscount > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-medium text-gray-500">Max Discount</span>
                        <span className="text-sm font-medium text-gray-700">₹{coupon.maxDiscount}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-gray-500">Offer</span>
                      <span className="text-sm font-bold text-blue-600">
                        Buy {coupon.bogo?.buyQuantity} Get {coupon.bogo?.getQuantity} Free
                      </span>
                    </div>
                    {coupon.bogo?.itemId?.name && (
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-medium text-gray-500">Item</span>
                        <span className="text-sm font-medium text-gray-700">{coupon.bogo.itemId.name}</span>
                      </div>
                    )}
                    {coupon.minOrderValue > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-medium text-gray-500">Min Order</span>
                        <span className="text-sm font-medium text-gray-700">₹{coupon.minOrderValue}</span>
                      </div>
                    )}
                  </>
                )}
                <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                  <span className="text-xs font-medium text-gray-500">Valid Until</span>
                  <span className="text-sm font-medium text-gray-700">{new Date(coupon.validUntil).toLocaleDateString()}</span>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-100">
                <button
                  className="px-3 py-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200 hover:scale-110 text-sm font-medium"
                  onClick={() => openModal(coupon)}
                  title="Edit"
                >
                  <span className="hidden sm:inline">Edit</span>
                  <span className="sm:hidden">✏️</span>
                </button>
                <button
                  className={`px-3 py-1.5 rounded-lg transition-colors duration-200 hover:scale-110 text-sm font-medium ${getCouponStatus(coupon).status === 'Expired'
                      ? 'text-gray-400 cursor-not-allowed opacity-50'
                      : getCouponStatus(coupon).status === 'Active'
                        ? 'text-orange-600 hover:bg-orange-50'
                        : 'text-green-600 hover:bg-green-50'
                    }`}
                  onClick={() => getCouponStatus(coupon).status !== 'Expired' && toggleCouponStatus(coupon._id)}
                  title={getCouponStatus(coupon).status === 'Expired' ? 'Coupon Expired - Cannot Toggle' : getCouponStatus(coupon).status === 'Active' ? 'Deactivate' : 'Activate'}
                  disabled={getCouponStatus(coupon).status === 'Expired'}
                >
                  <span className="hidden sm:inline">{getCouponStatus(coupon).status === 'Expired' ? 'Locked' : getCouponStatus(coupon).status === 'Active' ? 'Deactivate' : 'Activate'}</span>
                  <span className="sm:hidden">{getCouponStatus(coupon).status === 'Expired' ? '🔒' : getCouponStatus(coupon).status === 'Active' ? '⏸️' : '▶️'}</span>
                </button>
                <button
                  className="px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200 hover:scale-110 text-sm font-medium"
                  onClick={() => handleDelete(coupon._id)}
                  title="Delete"
                >
                  <span className="hidden sm:inline">Delete</span>
                  <span className="sm:hidden">🗑️</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {coupons.length === 0 && (
        <div className="bg-gray-50 rounded-lg p-12 text-center">
          <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">🎟️</span>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Coupons Created Yet</h3>
          <p className="text-gray-600 mb-6">Start by creating your first discount coupon to attract customers.</p>
          <button
            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg transform transition-all duration-200 hover:scale-105 flex items-center justify-center mx-auto gap-2"
            onClick={() => openModal()}
          >
            <span className="hidden sm:inline">+</span>
            <span className="sm:hidden">Create</span>
            <span className="hidden sm:inline">Create Your First Coupon</span>
          </button>
        </div>
      )}

      {/* Pagination */}
      {Math.ceil(coupons.length / itemsPerPage) > 1 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-sm text-gray-600">
              Showing {Math.min((currentPage - 1) * itemsPerPage + 1, coupons.length)}-{Math.min(currentPage * itemsPerPage, coupons.length)} of {coupons.length} coupons
            </div>
            <div className="flex gap-2 items-center">
              <button
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 text-gray-700 rounded-lg font-medium transition-colors duration-200"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              {[...Array(Math.ceil(coupons.length / itemsPerPage))].map((_, idx) => {
                const pageNum = idx + 1;
                const totalCouponPages = Math.ceil(coupons.length / itemsPerPage);
                if (
                  pageNum === 1 ||
                  pageNum === totalCouponPages ||
                  (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={pageNum}
                      className={`min-w-[36px] px-3 py-2 rounded-lg font-medium transition-all duration-200 ${pageNum === currentPage
                          ? 'bg-blue-500 text-white shadow-lg'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                        }`}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                  return <span key={pageNum} className="px-2 text-gray-400">...</span>;
                }
                return null;
              })}
              <button
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 text-gray-700 rounded-lg font-medium transition-colors duration-200"
                onClick={() => setCurrentPage(prev => Math.min(Math.ceil(coupons.length / itemsPerPage), prev + 1))}
                disabled={currentPage === Math.ceil(coupons.length / itemsPerPage)}
              >
                Next
              </button>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-600">Show</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-1 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <span className="text-gray-600">per page</span>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Coupon Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >

            {/* Header */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 flex justify-between items-center flex-shrink-0">
              <h2 className="text-xl font-bold">
                {selectedCoupon ? "Edit Coupon" : "Create Coupon"}
              </h2>

              <button
                className="text-white hover:bg-white/20 rounded-lg p-2"
                onClick={() => setShowModal(false)}
              >
                <span className="text-xl">×</span>
              </button>
            </div>

            <form
              onSubmit={handleSubmit}
              className="flex flex-col flex-1 overflow-hidden"
            >

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Coupon Code *</label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200"
                    value={couponForm.code}
                    onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
                    placeholder="e.g., SAVE20"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200"
                    value={couponForm.description}
                    onChange={(e) => setCouponForm({ ...couponForm, description: e.target.value })}
                    rows="2"
                    placeholder="Brief description of the offer"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Coupon Type *</label>
                  <select
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200"
                    value={couponForm.couponType}
                    onChange={(e) => setCouponForm({ ...couponForm, couponType: e.target.value })}
                  >
                    <option value="discount">Discount Coupon</option>
                    <option value="bogo">Buy X Get Y (BOGO)</option>
                  </select>
                  <p className="text-xs text-gray-600 mt-1">
                    {couponForm.couponType === 'discount' ? 'Percentage or flat discount on order' : 'Buy X items, get Y items free'}
                  </p>
                </div>

                {couponForm.couponType === 'discount' && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Discount Type *</label>
                        <select
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200"
                          value={couponForm.discountType}
                          onChange={(e) => setCouponForm({ ...couponForm, discountType: e.target.value })}
                        >
                          <option value="percentage">Percentage (%)</option>
                          <option value="flat">Flat Amount (₹)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Discount Value *</label>
                        <input
                          type="text"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200"
                          value={couponForm.discountValue}
                          inputMode="decimal"
                          placeholder="Enter discount value"
                          required
                          onChange={(e) => {
                            let value = e.target.value.replace(/[^0-9.]/g, ''); // only digits + dot

                            // allow only one decimal
                            const parts = value.split('.');
                            if (parts.length > 2) {
                              value = parts[0] + '.' + parts.slice(1).join('');
                            }

                            setCouponForm({
                              ...couponForm,
                              discountValue: value
                            });
                          }}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Min Order Value (₹)</label>
                        <input
                          type="text"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200"
                          value={couponForm.minOrderValue}
                          inputMode="decimal"
                          placeholder="Enter minimum order value"
                          onChange={(e) => {
                            let value = e.target.value.replace(/[^0-9.]/g, ''); // only digits + dot

                            // allow only one decimal
                            const parts = value.split('.');
                            if (parts.length > 2) {
                              value = parts[0] + '.' + parts.slice(1).join('');
                            }

                            setCouponForm({
                              ...couponForm,
                              minOrderValue: value
                            });
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Max Discount (₹)</label>
                        <input
                          type="text"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200"
                          value={couponForm.maxDiscount}
                          inputMode="decimal"
                          placeholder="Enter maximum discount"
                          onChange={(e) => {
                            let value = e.target.value.replace(/[^0-9.]/g, ''); // only digits + dot

                            // allow only one decimal
                            const parts = value.split('.');
                            if (parts.length > 2) {
                              value = parts[0] + '.' + parts.slice(1).join('');
                            }

                            setCouponForm({
                              ...couponForm,
                              maxDiscount: value
                            });
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}
                {couponForm.couponType === 'bogo' && (
                  <>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Select Menu Item *</label>
                      <select
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200"
                        value={couponForm.bogo.itemId}
                        onChange={(e) => setCouponForm({
                          ...couponForm,
                          bogo: { ...couponForm.bogo, itemId: e.target.value }
                        })}
                        required
                      >
                        <option value="">Select an item</option>
                        {menuItems.map((item) => (
                          <option key={item._id} value={item._id}>
                            {item.name} - ₹{item.discountPrice || item.price}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-600 mt-1">Choose the menu item for this BOGO offer</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Buy Quantity *</label>
                        <input
                          type="text"
                          inputMode="decimal"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200"
                          value={couponForm.bogo.buyQuantity}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || /^\d*$/.test(value)) {
                              setCouponForm({
                                ...couponForm,
                                bogo: { ...couponForm.bogo, buyQuantity: value }
                              });
                            }
                          }}
                          required
                        />
                        <p className="text-xs text-gray-600 mt-1">Number of items to buy</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Get Quantity *</label>
                        <input
                          type="text"
                          inputMode="decimal"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200"
                          value={couponForm.bogo.getQuantity}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || /^\d*$/.test(value)) {
                              setCouponForm({
                                ...couponForm,
                                bogo: { ...couponForm.bogo, getQuantity: value }
                              });
                            }
                          }}
                          required
                        />
                        <p className="text-xs text-gray-600 mt-1">Number of items to get free</p>
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Min Order Value (₹)</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200"
                        value={couponForm.minOrderValue}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                            setCouponForm({ ...couponForm, minOrderValue: value });
                          }
                        }}
                      />
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-amber-800">
                        <strong>Note:</strong> Buy {couponForm.bogo.buyQuantity} {menuItems.find(m => m._id === couponForm.bogo.itemId)?.name || 'item'}(s), get {couponForm.bogo.getQuantity} free.
                      </p>
                    </div>
                  </>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Valid From *</label>
                    <input
                      type="date"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200"
                      value={couponForm.validFrom}
                      onChange={(e) => setCouponForm({ ...couponForm, validFrom: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Valid Until *</label>
                    <input
                      type="date"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors duration-200"
                      value={couponForm.validUntil}
                      onChange={(e) => setCouponForm({ ...couponForm, validUntil: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="flex items-center gap-3 cursor-pointer p-4 bg-gray-50 rounded-lg border border-gray-200 transition-colors duration-200 hover:bg-gray-100">
                    <input
                      type="checkbox"
                      checked={couponForm.isActive}
                      onChange={(e) => setCouponForm({ ...couponForm, isActive: e.target.checked })}
                      className="w-5 h-5 text-purple-600 focus:ring-purple-500 rounded cursor-pointer"
                    />
                    <span className={`font-medium ${couponForm.isActive ? 'text-green-600' : 'text-red-600'}`}>
                      {couponForm.isActive ? '✓ Coupon is Active' : '✗ Coupon is Inactive'}
                    </span>
                  </label>
                  <p className="text-xs text-gray-600 mt-2">
                    {couponForm.isActive ? 'Customers can use this coupon' : 'Coupon is hidden from customers'}
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-6 py-4 border-t flex justify-end gap-3 flex-shrink-0">

                <button
                  type="button"
                  className="px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg"
                >
                  {selectedCoupon ? "Update" : "Create"} Coupon
                </button>

              </div>

            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponManagement;
